import {
  Pwerm2CalculationResultsDto,
  Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto,
} from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import { getTransactionsGroupedByCaseAndYear } from '@app/core/store/pwerm2-calculation-slice-selectors';

export type TransactionsByType = Record<
  string,
  Record<string, Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[]>
>;

export interface InstrumentTransactions {
  infusionTransactions: TransactionsByType;
  redemptionTransactions: TransactionsByType;
  cashPaidTransactions: TransactionsByType;
  dividendTransactions: TransactionsByType;
  containsInfusions: boolean;
  containsRedemptions: boolean;
  containsCashPaid: boolean;
  containsDividends: boolean;
  hasInterims: boolean;
}

export const getInstrumentTransactions = (
  results: Pwerm2CalculationResultsDto,
  instrumentId: string
): InstrumentTransactions => {
  const infusionTransactions = getTransactionsGroupedByCaseAndYear(
    results,
    instrumentId,
    (t) => t?.infusionTransactions
  );
  const redemptionTransactions = getTransactionsGroupedByCaseAndYear(
    results,
    instrumentId,
    (t) => t?.redemptionTransactions
  );
  const cashPaidTransactions = getTransactionsGroupedByCaseAndYear(
    results,
    instrumentId,
    (t) => t?.cashPaidTransactions
  );
  const dividendTransactions = getTransactionsGroupedByCaseAndYear(
    results,
    instrumentId,
    (t) => t?.dividendTransactions
  );

  const containsInfusions = hasTransactions(infusionTransactions);
  const containsRedemptions = hasTransactions(redemptionTransactions);
  const containsCashPaid = hasTransactions(cashPaidTransactions);
  const containsDividends = hasTransactions(dividendTransactions);

  return {
    infusionTransactions,
    redemptionTransactions,
    cashPaidTransactions,
    dividendTransactions,
    containsInfusions,
    containsRedemptions,
    containsCashPaid,
    containsDividends,
    hasInterims: containsInfusions || containsRedemptions || containsCashPaid || containsDividends,
  };
};

export const maxNumberOfTransactionsInYear = (
  transactionsByCaseAndYear: Record<
    string,
    Record<string, Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[]>
  >
) =>
  Math.max(
    0,
    ...Object.values(transactionsByCaseAndYear)
      .flatMap((t) => Object.values(t))
      .map((t) => t.length)
  );

export const hasTransactions = (
  transactions: Record<
    string,
    Record<string, Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[]>
  >
): boolean =>
  Object.values(transactions).some((caseTransactions) =>
    Object.values(caseTransactions).some((yearTransactions) => yearTransactions.length > 0)
  );
